export interface ColumnMapping {
  [key: string]: string
}

export interface ColumnInference {
  canonical: string
  type: 'string' | 'number'
  units: string
}

export interface ColumnInferenceResponse {
  [columnName: string]: ColumnInference
}

export interface ColumnTypeInfo {
  type: 'string' | 'number'
  units: string
}

export interface InferredColumnData {
  inferredColumnTitles: Record<string, string>
  columnToCanonical: Record<string, string>
  columnTypes: Record<string, ColumnTypeInfo>
  columnUnits: Record<string, string>
}

export interface GridData {
  columnDefs: Array<{
    field: string
    headerName: string
    sortable: boolean
    filter: boolean
    type: string
    valueFormatter?: (params: any) => string
    headerTooltip?: string
    cellClass?: (params: any) => string
  }>
  rowData: Array<Record<string, any>>
}

export interface OpenAIResponse {
  choices: Array<{
    message: {
      content: string
      role: string
    }
  }>
}

export interface TransientGridState {
  columnDefs?: GridData['columnDefs']
  rowData?: GridData['rowData']
  isDirty?: boolean
  lastSaved?: string
  localChanges?: {
    columns?: {
      [key: string]: {
        width?: number
        hide?: boolean
        sort?: string
        order?: number
      }
    }
    rows?: {
      [key: string]: {
        [key: string]: any
      }
    }
  }
}

export const columnSchema = {
  type: "object",
  properties: {
    // code: {
    //   type: "string",
    //   description: "Botanical code of the plant",
    //   colormap: "qualitative"
    // },
    // botanicalname: {
    //   type: "string", 
    //   description: "Scientific/botanical name of the plant",
    //   colormap: "qualitative"
    // },
    commonname: {
      type: "string",
      description: "This is the vernacular or everyday conversational name for a plant, such as 'Golden Wattle' or 'Lilly Pilly'. These names are often inconsistent and vary by location",
      colormap: "qualitative"
    },
    qty: {
      type: "number",
      description: "Total quantity of plants of this species",
      colormap: "sequential_centered"
    },
    maturewidth: {
      type: "number",
      description: "Maximum width (crown size) of vegetative canopy for a typical mature individual",
      colormap: "sequential"
    },
    matureheight: {
      type: "number",
      description: "Maximum height of a self-supporting species at maturity (not climbers whose height is dependent on the surrounding vegetation) and achieved by vegetative shoots.",
      colormap: "sequential"
    },
    maturesize: {
      type: "string",
      description: "The mature height × mature width of the plant",
      colormap: "sequential"
    },
    potsize: {
      type: "string",
      description: "Size of the container (e.g., T50mm, 75mm, 25ltr, 100ltr, 400ltr, etc.) - indicates the volume or dimensions of the growing container used for the plant",
      colormap: "qualitative"
    },
    spacing: {
      type: "string",
      description: "Recommended spacing between plants",
      colormap: "sequential"
    },
    density: {
      type: "string",
      description: "Planting density or plants per square meter",
      colormap: "sequential"
    },
    // width: {
    //   type: "number",
    //   description: "Current width of the plant",
    //   colormap: "sequential"
    // },
    // height: {
    //   type: "number",
    //   description: "Current height of the plant",
    //   colormap: "sequential"
    // },
    spread: {
      type: "string",
      description: "Spread or coverage of the plant",
      colormap: "sequential"
    },
    proportionpercentage: {
      type: "number",
      description: "Percentage proportion in the planting mix",
      colormap: "divergent"
    }
  }
} as const 

export const columnDisplayLabels: Record<string, string> = {
  qty: "Quantity",
  botanicalname: "Botanical Name",
  commonname: "Common Name",
  maturesize: "Mature Size",
  maturewidth: "Mature Width",
  matureheight: "Mature Height",
  potsize: "Pot Size",
  spacing: "Spacing",
  density: "Density",
  code: "Code",
  width: "Width",
  height: "Height",
  spread: "Spread",
  proportionpercentage: "Proportion (%)"
} as const 